import { zodResolver } from '@hookform/resolvers/zod'
import { useState } from 'react'
import { useForm } from 'react-hook-form'

import { env } from 'src/config/env'
import AuthSubmitButton from 'src/features/auth/components/auth-submit-button'
import AuthSubtitle from 'src/features/auth/components/auth-subtitle'
import AuthTitle from 'src/features/auth/components/auth-title'
import { Button } from 'src/shared/components/ui/buttons/button'
import { Form } from 'src/shared/components/ui/forms/form'
import { CodeFormField } from 'src/shared/components/ui/forms/form-fields/code'
import { InputFormField } from 'src/shared/components/ui/forms/form-fields/input'
import { useConsumerSignInStart, useConsumerSignInComplete } from 'src/shared/features/auth/api'
import { useToast } from 'src/shared/hooks/use-toast'
import {
  consumerLoginEmailSchema,
  type ConsumerLoginEmailFormData,
  consumerLoginOtpSchema,
  type ConsumerLoginOtpFormData,
} from 'src/shared/schemas/forms/consumer-auth'

import AuthCodeInput from '../auth-code-input'
import AuthInputFormField from '../auth-input-form-field'

interface ConsumerLoginFormProps {
  onSuccess: () => void
  variant?: 'default' | 'version2'
  onStepChange?: (step: 'email' | 'otp') => void
}

const ConsumerLoginForm = ({ onSuccess, variant = 'default', onStepChange }: ConsumerLoginFormProps) => {
  const [step, setStep] = useState<'email' | 'otp'>('email')
  const [signInData, setSignInData] = useState<any>(null)
  const { toast } = useToast()

  // Email form
  const emailForm = useForm<ConsumerLoginEmailFormData>({
    resolver: zodResolver(consumerLoginEmailSchema),
    mode: 'onSubmit',
    defaultValues: {
      email: '',
    },
  })

  // OTP form
  const otpForm = useForm<ConsumerLoginOtpFormData>({
    resolver: zodResolver(consumerLoginOtpSchema),
    mode: 'onSubmit',
    defaultValues: {
      otp: '',
    },
  })

  // Hooks for sign-in process
  const signInStart = useConsumerSignInStart()
  const signInComplete = useConsumerSignInComplete()

  const email = emailForm.getValues('email')

  // Handle email submission
  const handleEmailSubmit = async (data: ConsumerLoginEmailFormData) => {
    try {
      const result = await signInStart.mutateAsync(data.email)
      setSignInData(result)
      setStep('otp')
      onStepChange?.('otp')

      toast({
        title: 'Verification code sent',
        description: 'Please check your email for the verification code',
        duration: 5000,
      })
    } catch (error) {
      console.error('Sign-in start error:', error)

      if (error instanceof Error) {
        // For user not found, give a clearer message
        if (error.name === 'UserNotFoundException') {
          toast({
            title: 'Account not found',
            description: 'No account exists with this email. Please sign up first.',
            variant: 'destructive',
          })
          return
        }

        toast({
          title: `Sign in failed: ${error.name}`,
          description: error.message || 'Failed to start sign in process',
          variant: 'destructive',
        })
      } else {
        toast({
          title: 'Sign in failed',
          description: 'An unexpected error occurred. Please try again.',
          variant: 'destructive',
        })
      }
    }
  }

  // Handle OTP submission
  const handleOtpSubmit = async (data: ConsumerLoginOtpFormData) => {
    try {
      await signInComplete.mutateAsync({
        challengeResponse: data.otp,
        signInResult: signInData,
      })

      toast({
        title: 'Welcome back!',
        description: 'You have successfully signed in',
        duration: 5000,
      })

      onSuccess()
    } catch (error) {
      console.error('OTP verification error:', error)

      if (error instanceof Error) {
        toast({
          title: `Verification failed: ${error.name}`,
          description: error.message || 'Failed to verify code',
          variant: 'destructive',
        })
      } else {
        toast({
          title: 'Verification failed',
          description: 'An unexpected error occurred. Please try again with a new code.',
          variant: 'destructive',
        })
      }
    }
  }

  // Go back to email input
  const handleBack = () => {
    setStep('email')
    onStepChange?.('email')
    setSignInData(null)
  }

  if (variant === 'version2') {
    return (
      <div className="mx-auto mt-4 w-full max-w-[500px]">
        {step === 'email' ? (
          <>
            <AuthTitle title="Login" />
            <AuthSubtitle description={"You'll need to confirm this email later."} />
            <Form {...emailForm}>
              <form onSubmit={emailForm.handleSubmit(handleEmailSubmit)} className="flex flex-col gap-3">
                <AuthInputFormField
                  control={emailForm.control}
                  name="email"
                  label="Email"
                  type="email"
                  placeholder="Email"
                />
                {/* <div className="mt-0">
                  <p className="text-center text-xs text-muted">
                    We&apos;ll email you a code to confirm your email.{' '}
                    <a
                      href={env.LINK_TO_PRIVACY_POLICY}
                      target="_blank"
                      className="font-medium text-muted underline"
                      rel="noreferrer"
                    >
                      Privacy Policy
                    </a>
                  </p>
                </div> */}
                <AuthSubmitButton type="submit" isLoading={signInStart.isPending} className="mt-4">
                  {signInStart.isPending ? 'Sending Code...' : 'Continue'}
                </AuthSubmitButton>
              </form>
            </Form>
          </>
        ) : (
          <Form {...otpForm}>
            <form onSubmit={otpForm.handleSubmit(handleOtpSubmit)} className="mx-auto max-w-[500px] space-y-4">
              <div>
                <AuthTitle title="Confirm your email" />
                <AuthSubtitle
                  description={`You've received a code at ${
                    email || 'email address'
                  }. Please check your inbox and spam folders to retrieve it.`}
                />
                <AuthCodeInput
                  isConsumerLogin={true}
                  control={otpForm.control}
                  name="otp"
                  className="pb-2"
                  inputClassName="h-[55px] border-none bg-accent rounded-[12px]"
                />
              </div>
              <div className="flex flex-col gap-2">
                <AuthSubmitButton isLoading={signInComplete.isPending}>
                  {signInComplete.isPending ? 'Verifying...' : 'Verify & sign in'}
                </AuthSubmitButton>

                <Button
                  type="button"
                  variant="ghost"
                  className="mt-2 text-sm text-muted hover:underline"
                  onClick={handleBack}
                >
                  Try a different email address
                </Button>
              </div>
            </form>
          </Form>
        )}
      </div>
    )
  }

  return (
    <div className="mt-6 space-y-6">
      {step === 'email' ? (
        <>
          <h2 className="text-center text-3xl font-bold text-primary">Welcome back</h2>
          <Form {...emailForm}>
            <form onSubmit={emailForm.handleSubmit(handleEmailSubmit)} className="space-y-4">
              <InputFormField
                control={emailForm.control}
                name="email"
                label="Email address"
                type="email"
                placeholder="Email address"
                required
              />
              <div className="mt-4">
                <p className="text-xs text-muted">
                  We&apos;ll email you a code to confirm your email.{' '}
                  <a href={env.LINK_TO_PRIVACY_POLICY} className="font-medium text-primary underline">
                    Privacy Policy
                  </a>
                </p>
              </div>
              <Button type="submit" className="w-full py-4" isLoading={signInStart.isPending}>
                {signInStart.isPending ? 'Sending Code...' : 'Continue'}
              </Button>
            </form>
          </Form>
        </>
      ) : (
        <Form {...otpForm}>
          <form onSubmit={otpForm.handleSubmit(handleOtpSubmit)} className="space-y-4">
            <h3 className="text-center text-xl font-medium text-primary">Confirm your email</h3>
            <p className="text-center text-sm text-muted">
              You&apos;ve received a code at your email address. Please check your inbox and spam folders to retrieve
              it.
            </p>

            <CodeFormField control={otpForm.control} name="otp" className="py-2" length={8} />

            <div className="flex flex-col gap-2">
              <Button type="submit" className="w-full py-4 " isLoading={signInComplete.isPending}>
                {signInComplete.isPending ? 'Verifying...' : 'Verify & sign in'}
              </Button>

              <Button
                type="button"
                variant="ghost"
                className="mt-2 text-sm text-muted hover:underline"
                onClick={handleBack}
              >
                Try a different email address
              </Button>
            </div>
          </form>
        </Form>
      )}
    </div>
  )
}

export default ConsumerLoginForm
